import { useCallback, useEffect, useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import type { ViewToken } from 'react-native';
import { ColorValues, Flex, List, useScreenSize } from '@banhala/ui-kit';
import type { MarketCarouselEntity, MarketCarouselProps } from './types';
import { ThreeImageHeader } from './components/ThreeImageHeader';
import { MarketCard } from './components/MarketCard';
import { FavoriteButton } from './components/FavoriteButton';
import { isNil } from '../../../../utils';
import { useOpenDeeplink } from '../../../../hooks';
import { useLogAnalyticsEvent } from '../../../../hooks/useLogAnalyticsEvent';

const INFO_HEIGHT = 68;
const CARD_BORDER_RADIUS = 4;

const VIEWABILITY_CONFIG = { itemVisiblePercentThreshold: 50 };

const keyExtractor = (_item: MarketCarouselEntity, index: number) =>
  index.toString();

interface ThreeImageCardProps extends MarketCarouselEntity {
  cardWidth: number;
  itemIdx: number;
}

const ThreeImageCard = ({
  item,
  logging,
  cardWidth,
  itemIdx,
}: ThreeImageCardProps) => {
  const { sno, profile, name, description, goods_images, favorite } = item;
  const { openDeeplink } = useOpenDeeplink();
  const logAnalyticsEvent = useLogAnalyticsEvent();

  const handlePressCard = useCallback(() => {
    logAnalyticsEvent({
      name: 'CLICK_MARKET',
      analytics: logging.analytics,
      ably_only_log: { ...logging.ably_only_log, event_name: 'click' },
    });
    openDeeplink(`ably://markets/${sno}/`);
  }, [openDeeplink, sno, logging, logAnalyticsEvent]);

  return (
    <View style={styles.cardPadding}>
      <View style={[styles.cardShadow, { borderRadius: CARD_BORDER_RADIUS }]}>
        <Flex
          width={cardWidth}
          borderRadius={CARD_BORDER_RADIUS}
          overflow="hidden"
          onPress={handlePressCard}
        >
          <ThreeImageHeader images={goods_images} cardWidth={cardWidth} />

          <MarketCard
            sno={sno}
            profile={profile}
            name={name}
            description={description}
            logging={logging}
            profileSize={40}
            nameType="subtitle3"
            descriptionType="body5"
            descriptionColor="content_secondary"
            height={INFO_HEIGHT}
            paddingHorizontal={14}
            paddingVertical={12}
            pressable={false}
            showChevron={false}
            itemIdx={itemIdx}
            rightElement={
              <FavoriteButton
                sno={sno}
                isFavorited={favorite?.is_favorited ?? false}
                logging={logging}
                testID={`마켓큐레이션찜?index=${itemIdx}`}
              />
            }
          />
        </Flex>
      </View>
    </View>
  );
};

export const ThreeImageMarketCarousel = ({
  entity,
  isVisible,
  isAwaitingDataChange,
}: MarketCarouselProps) => {
  const { width: screenWidth } = useScreenSize();
  const cardWidth = Math.round(screenWidth * 0.86);

  // GoodsCarousel.useLogDisplayGoods 패턴 — inner FlatList 가 부모(AtomicRenderer)
  // viewport 와 무관하게 viewable 을 보고하므로, emit 시점에 부모 가시성으로 한번 더
  // 가드 + isVisible false→true 전이 시 캐시 기반 재emit 으로 노출 결손 방지.
  const loggedSnosRef = useRef<Set<number>>(new Set());
  const lastViewableItemsRef = useRef<ViewToken[]>([]);
  const isVisibleRef = useRef(isVisible);
  isVisibleRef.current = isVisible;
  const isAwaitingDataChangeRef = useRef(isAwaitingDataChange);
  isAwaitingDataChangeRef.current = isAwaitingDataChange;

  const logAnalyticsEvent = useLogAnalyticsEvent();

  const emitForViewable = useCallback(
    (viewableItems: ViewToken[]) => {
      if (isVisibleRef.current === false) return;
      if (isAwaitingDataChangeRef.current === true) return;
      viewableItems.forEach(({ isViewable, item }) => {
        if (!isViewable || isNil(item)) return;
        const entityItem = item as MarketCarouselEntity;
        const sno = entityItem.item.sno;
        if (loggedSnosRef.current.has(sno)) return;
        loggedSnosRef.current.add(sno);
        logAnalyticsEvent({
          name: 'DISPLAY_MARKET',
          analytics: {
            ...entityItem.logging.analytics,
            IS_FAVORITED: entityItem.item.favorite?.is_favorited ? 1 : 0,
          },
          ably_only_log: entityItem.logging.ably_only_log,
        });
      });
    },
    [logAnalyticsEvent],
  );

  useEffect(() => {
    if (isVisible && !isAwaitingDataChange) {
      emitForViewable(lastViewableItemsRef.current);
    }
  }, [isVisible, isAwaitingDataChange, emitForViewable]);

  const onViewableItemsChanged = useCallback(
    ({ viewableItems }: { viewableItems: ViewToken[] }) => {
      lastViewableItemsRef.current = viewableItems;
      emitForViewable(viewableItems);
    },
    [emitForViewable],
  );

  const renderItem = useCallback(
    ({ item, index }: { item: MarketCarouselEntity; index: number }) => (
      <ThreeImageCard
        item={item.item}
        logging={item.logging}
        cardWidth={cardWidth}
        itemIdx={index}
      />
    ),
    [cardWidth],
  );

  if (isNil(entity) || entity.length === 0) {
    return null;
  }

  return (
    <List
      horizontal
      data={entity}
      renderItem={renderItem}
      keyExtractor={keyExtractor}
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.scrollContent}
      onViewableItemsChanged={onViewableItemsChanged}
      viewabilityConfig={VIEWABILITY_CONFIG}
    />
  );
};

const styles = StyleSheet.create({
  scrollContent: {
    paddingHorizontal: 12,
    paddingBottom: 20,
    gap: 12,
  },
  cardPadding: {
    paddingTop: 4,
    paddingBottom: 4,
  },
  cardShadow: {
    backgroundColor: ColorValues.white,
    shadowColor: ColorValues.black,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
});
